import React from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import CulmoraMark from "../CulmoraMark";

const NAV_ALL = [
  { label: "Product", to: "/#product" },
  { label: "System", to: "/#system" },
  { label: "How It Works", to: "/#how-it-works" },
  { label: "Trust", to: "/#trust" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/contact" },
];

const testId = (label, prefix) =>
  `${prefix}-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

function BrandMark({ testIdPrefix = "header" }) {
  return (
    <span className="culmora-brand-lockup flex items-center w-fit leading-none">
      <CulmoraMark className="culmora-brand-mark shrink-0" />
      <span className="flex flex-col items-start justify-center min-w-0">
        <span className="culmora-brand-wordmark whitespace-nowrap" data-testid={`${testIdPrefix}-wordmark`}>
          CULMORA OS
        </span>
        <span className="culmora-brand-tagline whitespace-nowrap" data-testid={`${testIdPrefix}-tagline`}>
          Professional AI Workspace
        </span>
      </span>
    </span>
  );
}

function Brand() {
  return (
    <Link to="/" data-testid="header-brand" className="w-fit" aria-label="Culmora OS — Professional AI Workspace, home">
      <BrandMark />
    </Link>
  );
}

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const closeRef = React.useRef(null);
  const openerRef = React.useRef(null);
  const navDialogRef = React.useRef(null);

  React.useEffect(() => setOpen(false), [location.pathname, location.hash]);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      closeRef.current?.focus();
    } else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        openerRef.current?.focus();
      }
      if (e.key === "Tab") {
        const first = closeRef.current;
        const links = Array.from(navDialogRef.current?.querySelectorAll("a[href]") || []);
        const last = links.at(-1) || first;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header data-testid="site-header" className="culmora-site-header sticky top-0 z-40 w-full" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <a href="#main" className="z-skip">Skip to content</a>
      <div className="z-page py-3 md:py-4">
        <div data-testid="header-shell" className="culmora-header-shell flex items-center justify-between gap-5">
          <Brand />

          <div className="hidden dsk:flex items-center justify-end gap-5 xl:gap-6 min-w-0">
            <nav aria-label="Primary" className="flex items-center gap-4 xl:gap-5">
              {NAV_ALL.map((item) => (
                <a key={item.label} href={item.to} className="culmora-nav-link" data-testid={testId(item.label, "desktop-nav")}>
                  {item.label}
                </a>
              ))}
            </nav>
            <a href="/#request" className="culmora-header-cta" data-testid="desktop-nav-request">Request Early Access</a>
          </div>

          <button ref={openerRef} type="button" aria-expanded={open} aria-controls="site-nav" aria-label={open ? "Close menu" : "Open menu"} onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" className="dsk:hidden culmora-header-control inline-flex items-center justify-center rounded-full border border-[hsl(var(--z-border-strong))] text-[hsl(var(--z-text))] hover:border-[hsl(var(--z-teal))]/60 transition-colors shrink-0">
            <Menu size={20} aria-hidden="true" />
          </button>
        </div>
      </div>

      {open && createPortal(
        <>
          <div aria-hidden="true" onClick={() => setOpen(false)} className="fixed inset-0 z-[55] bg-[hsl(var(--z-black))]/70 backdrop-blur-sm" data-testid="menu-backdrop" />
          <div ref={navDialogRef} id="site-nav" data-testid="mobile-nav-modal" role="dialog" aria-modal="true" aria-label="Site menu" className="fixed inset-0 z-[60] bg-[hsl(var(--z-black))] flex flex-col">
            <div className="flex items-center justify-between px-[22px] border-b border-[hsl(var(--z-border))]" style={{ minHeight: "var(--culmora-header-height)", paddingTop: "env(safe-area-inset-top)" }}>
              <BrandMark testIdPrefix="mobile-nav" />
              <button ref={closeRef} type="button" aria-label="Close menu" onClick={() => { setOpen(false); openerRef.current?.focus(); }} data-testid="mobile-nav-close" className="culmora-header-control inline-flex items-center justify-center rounded-full border border-[hsl(var(--z-border-strong))] text-[hsl(var(--z-text))] hover:border-[hsl(var(--z-teal))]/60 transition-colors">
                <X size={19} aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Site" className="w-full flex-1 flex flex-col justify-center py-8 px-[22px] overflow-y-auto">
              {NAV_ALL.map((item) => (
                <a key={item.label} href={item.to} onClick={() => setOpen(false)} className="py-5 font-display text-[26px] text-[hsl(var(--z-text))] border-b border-[hsl(var(--z-border))] hover:text-[hsl(var(--z-teal))] transition-colors" data-testid={testId(item.label, "mobile-nav")}>{item.label}</a>
              ))}
              <a href="/#request" onClick={() => setOpen(false)} className="mt-8 z-btn-ghost w-fit" data-testid="mobile-nav-request">Request Early Access</a>
            </nav>
          </div>
        </>, document.body,
      )}
    </header>
  );
}
